"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollaCommandManager = void 0;
const error_types_1 = require("../types/error_types");
const commands_1 = require("../commands");
const command_1 = require("./command");
const types_1 = require("./types");
const store_1 = require("../exports/store");
const model_1 = require("../model");
const types_2 = require("../types");
const cell_format_checker_1 = require("../cell_format_checker");
const link_integrity_checker_1 = require("../link_integrity_checker/link_integrity_checker");
const player_1 = require("../modules/shared/player");
class CollaCommandManager {
    constructor(_listener = {}, store) {
        this._listener = _listener;
        this.store = store;
        this._commands = {};
        this.recFlag = false; //录制标志
        this.recordCommands = [];
        const commandNames = Object.keys(commands_1.COLLA_COMMAND_MAP);
        commandNames.forEach(commandName => {
            this.register(commandName, commands_1.COLLA_COMMAND_MAP[commandName]);
        });
        this.cellFormatChecker = new cell_format_checker_1.CellFormatChecker(store);
        this.linkIntegrityChecker = new link_integrity_checker_1.LinkIntegrityChecker(store);
    }
    register(name, commandDef) {
        if (this._commands[name]) {
            console.warn(`the command name ${name} is registered and will be unregistered`, this._commands[name]);
            this.unregister(name);
        }
        this._commands[name] = new command_1.CollaCommand(commandDef, name);
    }
    unregister(name) {
        if (this._commands[name]) {
            delete this._commands[name];
        }
    }
    hasCommand(name) {
        return !!this._commands[name];
    }
    /**
     * passes the final generated op into the callback function that executes the op
     */
    didExecutedHook(datasheetOpsCollects) {
        this._listener.handleCommandExecuted && this._listener.handleCommandExecuted(datasheetOpsCollects);
    }
    execute(options) {
        const optionsFull = this.fullFillOptions(options);
        if (this.recFlag) {
            this.recordCommands.push(options);
        }
        let ret = this._execute(optionsFull);
        if (ret === null) {
            ret = {
                resourceId: optionsFull.resourceId,
                resourceType: optionsFull.resourceType,
                result: types_1.ExecuteResult.None,
            };
        }
        else if (ret.result === types_1.ExecuteResult.Fail) {
            console.error('ExecuteResult.Fail: ', ret);
        }
        return ret;
    }
    /**
     * @desc tool method, passing in the basic command option and returning structured parameters
     * */
    fullFillOptions(options) {
        let resourceId = undefined;
        // If you don't fill in resourceType, the default is datasheet
        // TODO: fill in resourceType for global modification
        let resourceType = types_2.ResourceType.Datasheet;
        // TODO: Change this to resourceId later;
        if ('datasheetId' in options) {
            resourceId = options.datasheetId;
        }
        if ('resourceId' in options) {
            resourceId = options.resourceId;
            resourceType = options.resourceType;
        }
        if (!resourceId) {
            resourceId = store_1.Selectors.getActiveDatasheetId(this._getContext().model);
        }
        return Object.assign(Object.assign({}, options), { resourceId,
            resourceType });
    }
    _execute(options) {
        const name = options.cmd;
        const command = this._commands[name];
        if (!command) {
            return null;
        }
        let ret = null;
        const context = this._getContext();
        try {
            ret = command.execute(context, options);
        }
        catch (e) {
            player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                error: new Error(`command execution error: ${e.message}`),
                metaData: {
                    resourceId: options.resourceId,
                    resourceType: options.resourceType,
                },
            });
            this._listener.handleCommandExecuteError &&
                this._listener.handleCommandExecuteError({
                    type: error_types_1.ErrorType.CollaError,
                    code: error_types_1.ErrorCode.CommandExecuteFailed,
                    message: e.message,
                });
            return {
                resourceId: options.resourceId,
                resourceType: options.resourceType,
                result: types_1.ExecuteResult.Fail,
                reason: types_1.ExecuteFailReason.ActionError,
            };
        }
        if (!ret) {
            return ret;
        }
        if (ret.result === types_1.ExecuteResult.Fail) {
            console.error('Execute "%s" Error', name, { error: ret, options });
            return ret;
        }
        const flushedActions = context.ldcMaintainer.flushLinkedActions(context.model);
        const memberFieldAction = context.memberFieldMaintainer.flushMemberAction(context.model);
        if (memberFieldAction.length) {
            ret.actions.push(...memberFieldAction);
        }
        if (ret.linkedActions) {
            ret.linkedActions.push(...flushedActions);
        }
        else {
            ret.linkedActions = flushedActions;
        }
        return this._executeActions(name, ret, types_1.ExecuteType.Execute);
    }
    _getContext() {
        // Each time the context is executed, a new maintainer is initialized to maintain the data consistency of the associated field cell
        return {
            model: this.store.getState(),
            ldcMaintainer: new model_1.LinkedDataConformanceMaintainer(),
            memberFieldMaintainer: new model_1.MemberFieldMaintainer(),
            fieldMapSnapshot: {},
        };
    }
    // TODO: Execution failure requires toast notification
    _executeActions(cmd, ret, executeType) {
        const { actions, resourceId, resourceType, linkedActions, fieldMapSnapshot } = ret;
        const command = this._commands[cmd];
        const context = this._getContext();
        if (!command || !resourceId) {
            console.error("can't find command or resource", command, resourceId);
            return null;
        }
        if (executeType === types_1.ExecuteType.Redo && !command.canRedo(context, actions)) {
            return null;
        }
        if (executeType === types_1.ExecuteType.Undo && !command.canUndo(context, actions)) {
            return null;
        }
        if (actions.length === 0) {
            return null;
        }
        if (this.addUndoStack && command.undoable()) {
            this.addUndoStack(cmd, ret, executeType);
        }
        let _actions = actions;
        const _fieldMapSnapshot = fieldMapSnapshot && Object.assign({}, fieldMapSnapshot);
        if (resourceType === types_2.ResourceType.Datasheet) {
            // This is adjusted to check all ops involved in cell writing, one is to judge the type, and if the type is consistent, check the format
            _actions = this.cellFormatChecker.parse(_actions, resourceId, _fieldMapSnapshot);
            if (executeType === types_1.ExecuteType.Redo || executeType === types_1.ExecuteType.Undo) {
                _actions = _fieldMapSnapshot ? _actions : this.linkIntegrityChecker.parse(_actions, resourceId, linkedActions);
            }
        }
        const _cmd = executeType === types_1.ExecuteType.Undo ? `UNDO:${cmd}` : cmd;
        const operation = {
            cmd: _cmd,
            actions: _actions,
            isMainDst: true
        };
        const fieldTypeMap = {};
        if (_fieldMapSnapshot && Object.keys(_fieldMapSnapshot).length) {
            for (const id in _fieldMapSnapshot) {
                const field = _fieldMapSnapshot[id];
                fieldTypeMap[id] = field.type;
            }
            operation.fieldTypeMap = fieldTypeMap;
        }
        // Collect the op; generated by cmd and send it out at one time
        const datasheetOpsCollects = [];
        datasheetOpsCollects.push({
            resourceId,
            resourceType,
            operations: [(linkedActions === null || linkedActions === void 0 ? void 0 : linkedActions.length) ? Object.assign(Object.assign({}, operation), { mainLinkDstId: resourceId }) : operation],
        });
        if (linkedActions) {
            linkedActions.forEach(lCmd => {
                if (!lCmd.actions.length) {
                    return;
                }
                const op = {
                    cmd: _cmd,
                    actions: lCmd.actions,
                    mainLinkDstId: resourceId,
                };
                datasheetOpsCollects.push({
                    resourceId: lCmd.datasheetId,
                    resourceType,
                    operations: [op],
                });
            });
        }
        //datasheetOpsCollects.splice(0,1)
        // After the op is applied, perform some hook operations.
        this.didExecutedHook(datasheetOpsCollects);
        return {
            result: types_1.ExecuteResult.Success,
            resourceId,
            resourceType,
            data: ret.data,
            operation,
            executeType,
            linkedActions,
            resourceOpsCollects: datasheetOpsCollects,
        };
    }
    //开始录制
    startRec() {
        this.recFlag = true;
        this.recordCommands = [];
    }
    //停止录制
    endRec() {
        this.recFlag = false;
        return this.recordCommands;
    }
}
exports.CollaCommandManager = CollaCommandManager;
//# sourceMappingURL=command_manager.js.map